import { useEffect, useState, useContext, createContext } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const Ctx = createContext({ features: new Set(), loading: true });

export function FeaturesProvider({ children }) {
  const { user, companyId } = useAuth();
  const [features, setFeatures] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadedCompanyId, setLoadedCompanyId] = useState(null);

  useEffect(() => {
    if (!user || !companyId) { setFeatures(new Set()); setLoadedCompanyId(null); setLoading(false); return; }
    let cancelled = false;
    const refreshFeatures = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/subscriptions/current");
        if (!cancelled) { setFeatures(new Set(data.features || [])); setLoadedCompanyId(companyId); }
      } catch { if (!cancelled) { setFeatures(new Set()); setLoadedCompanyId(companyId); } }
      finally { if (!cancelled) setLoading(false); }
    };
    refreshFeatures();
    window.addEventListener("company-features-updated", refreshFeatures);
    return () => { cancelled = true; window.removeEventListener("company-features-updated", refreshFeatures); };
  }, [user, companyId]);

  return <Ctx.Provider value={{ features, loading, loadedCompanyId }}>{children}</Ctx.Provider>;
}

/** true if user is the platform root admin OR the active company has the code. */
export function useFeatureAccess(code) {
  const { user, companyId } = useAuth();
  const { features, loading, loadedCompanyId } = useContext(Ctx);
  const waitingForCompany = !!user && !!companyId && loadedCompanyId !== companyId;
  return { enabled: !!user?.is_root || features.has(code), loading: !user?.is_root && !!user && (!companyId || loading || waitingForCompany) };
}

export function useFeature(code) {
  return useFeatureAccess(code).enabled;
}
